import { ReactNode } from "react";

import Header from "components/Header";
import HeaderPhone from "components/HeaderPhone";
import LegalLinks from "components/LegalLinks";
import ScreenChrome from "components/ScreenChrome";
import LogoWithType from "svg/logo-with-type.svg";

import { splitTitle, type AuthShellVariant } from "./auth-shell";
import { AuthShellProvider } from "./AuthShellContext";
import { useAuthShellVariant } from "./useAuthShellVariant";

function Footer() {
  return (
    <div className="my-8 space-y-2 text-center">
      <LegalLinks className="flex justify-center space-x-4 text-sm text-gray-500" />

      <div className="text-sm text-gray-500">
        Copyright © 2020–{new Date().getFullYear()} · After Services, Inc.
      </div>
    </div>
  );
}

type AuthLayoutProps = {
  title: string;
  description: string;
  children: ReactNode;
  /** The line to call from this screen, shown in the quote-flow header. */
  headerPhone?: { href: string; label: string };
  /** Wears this chrome whatever the flag says. */
  variant?: AuthShellVariant;
  /** Set where the description would be, for a first line that is not copy. */
  subheader?: ReactNode;
  /** Shows a bar in the heading's place while the title is not yet known. */
  titlePlaceholder?: boolean;
  /** The gap between the lines under the heading and the screen's content:
   * the page's own, or the one a screen's blocks keep among themselves. */
  bodySpacing?: "page" | "screen";
};

const BODY_SPACING_CLASS = { page: "mt-8", screen: "mt-6" };

function QuoteFlowFooter() {
  return (
    <div className="mx-auto w-full max-w-[760px] space-y-2 px-4 pb-8 text-center 2xs:px-5 sm:px-10">
      <LegalLinks className="flex justify-center space-x-4 text-sm text-oat-700" />
      <div className="text-sm text-oat-700">
        Copyright © 2020–{new Date().getFullYear()} · After Services, Inc.
      </div>
    </div>
  );
}

function QuoteFlowAuthLayout({
  title,
  description,
  children,
  headerPhone,
  subheader,
  titlePlaceholder,
  bodySpacing = "page",
}: AuthLayoutProps) {
  const { titleA, titleB } = splitTitle(title);
  const chrome = (
    <ScreenChrome
      kicker=""
      titleA={titleA}
      titleB={titleB}
      blurb={description}
      subheader={subheader}
      titlePlaceholder={titlePlaceholder}
    >
      <div className={BODY_SPACING_CLASS[bodySpacing]}>{children}</div>
    </ScreenChrome>
  );
  return (
    <div className="flex min-h-screen flex-col bg-[#FBF7EC]">
      <Header
        quoteFlow
        quoteFlowWidth="form"
        actions={headerPhone && <HeaderPhone line={headerPhone} />}
      />
      {chrome}
      <QuoteFlowFooter />
    </div>
  );
}

function ClassicAuthLayout({
  title,
  description,
  children,
  subheader,
}: AuthLayoutProps) {
  return (
    <>
      <div className="flex min-h-screen flex-col lg:flex-row">
        {/* Left side - Login Form */}
        <div className="flex h-full w-full grow flex-col justify-center px-6 py-12 lg:h-screen lg:w-2/5 lg:px-8">
          <div className="mx-auto h-full w-full max-w-sm">
            <div className="flex h-full flex-col justify-between">
              <div className="flex h-full flex-col justify-center">
                <div>
                  <div className="relative text-center">
                    <div className="mx-auto mb-8 w-28">
                      <LogoWithType />
                    </div>
                    <h1 className="mb-2 text-3xl font-bold text-soil">
                      {title}
                    </h1>
                    <p className="mb-8 text-base text-gray-600">
                      {description}
                    </p>
                    {subheader && <div className="mb-8">{subheader}</div>}
                  </div>
                  {children}
                </div>
              </div>

              <div className="hidden lg:block">
                <Footer />
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Illustration & Message */}
        <div className="hidden w-full items-center justify-center overflow-hidden bg-olive lg:flex lg:w-3/5 lg:px-16 lg:py-12">
          {/* Content Box with Illustrations */}
          <div className="relative w-full px-4 md:mx-auto md:max-w-lg">
            {/* Top Left Flowers */}
            <img
              src="public/bg_flowers_tl.png"
              alt=""
              className="absolute -top-10 left-2 h-48 w-auto scale-150 object-contain md:-top-12"
            />

            {/* Bottom Right Flowers */}
            <img
              src="public/bg_flowers_br.png"
              alt=""
              className="absolute -bottom-24 -right-10 h-48 w-auto -rotate-12 object-contain"
            />

            {/* Content */}
            <div className="relative w-full rounded-xl bg-white p-8 text-center shadow-lg md:p-12">
              <h2 className="mb-2 font-serif text-4xl font-bold text-soil">
                We're here to guide,
              </h2>
              <h2 className="mb-6 text-4xl font-bold text-evergreen">
                not direct
              </h2>
              <p className="text-xl text-soil text-opacity-80">
                Support and guidance, always within reach—whenever you need it.
              </p>
            </div>
          </div>
        </div>
        <div className="block lg:hidden">
          <Footer />
        </div>
      </div>
    </>
  );
}

/** The shell for every sign-in and account-link screen. Renders the
 * quote-flow chrome when the `auth-restyle` flag is on or the screen is
 * in login-grant mode, and the original two-column layout otherwise. */
export default function AuthLayout(props: AuthLayoutProps) {
  const shellVariant = useAuthShellVariant();
  const variant = props.variant ?? shellVariant;
  return (
    <AuthShellProvider value={variant}>
      {variant === "quote_flow" ? (
        <QuoteFlowAuthLayout {...props} />
      ) : (
        <ClassicAuthLayout {...props} />
      )}
    </AuthShellProvider>
  );
}
